<template>
  <AtomicUiNotification
    v-if="addToBagError"
    severity="error"
    role="alert"
    :closable="false"
    class="margin-top-s margin-bottom-xxs"
    @close="clearAddToBagError"
  >
    {{ addToBagError }}
  </AtomicUiNotification>
  <AtomicUiNotification v-if="hasError" class="margin-top-m error-container" severity="error" role="alert" :closable="false">
    {{ hasError }}
  </AtomicUiNotification>
  <div v-else class="guest-list-page">
    <AtomicUiBreadcrumb :model="breadcrumbItems" class="margin-top-xs margin-bottom-xs" />
    <div class="grid-x padding-bottom-xxs wishlist-header-container">
      <div :class="WISHLIST_TITLE_CLASS">Your temporary wish list</div>
      <div class="grid-x column-gap align-bottom">
        <div class="body">({{ numberOfWishlistItems }})</div>
        <div class="large">Default</div>
      </div>
    </div>

    <div class="margin-top-m small-margin-bottom-xl large-margin-bottom-xxl">
      <div class="guest-content-block cell text-center border-thickness padding-top-m padding-bottom-m padding-left-l padding-right-l">
        <div class="grid-x grid-margin-x align-middle">
          <div class="cell text-center">
            <h2 class="heavy margin-top-z">Want to keep your list?</h2>
            <p class="float-center">Sign in or create an account to save it, share it, get sale alerts & more.</p>
            <div class="action-button margin-top-xs">
              <button class="primary button" @click="goToSignIn">Sign in</button>
              <button class="secondary button" @click="goToCreateAccount">
                {{ CREATE_ACCOUNT_BUTTON }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <LazyWishlistFilters v-if="!isWishlistLoading && !hasError && currentWishlistItems?.length > 0" @filter-change="handleFilterChange" />

    <div v-if="numberOfFilteredItems" class="grid-container padding-z small-margin-bottom-xl large-margin-bottom-xxl">
      <div class="grid-x grid-margin-x grid-margin-y align-middle product-grid-x">
        <div
          v-for="(item, index) in filteredWishlistItems"
          :key="`guest-${item.id}`"
          class="cell small-6 medium-4 large-3 small-margin-top-s large-margin-top-m"
        >
          <ProductCard :item="item" :index="index" @add-to-bag-error="handleAddToBagError" />
        </div>
      </div>
    </div>
    <LazyProsWrapper v-else class="small-margin-bottom-xl large-margin-bottom-xxl" />
  </div>
</template>

<script setup>
import { Cookie } from '@vue-util/cookie';
import { WISHLIST_TITLE_CLASS, CREATE_ACCOUNT_BUTTON } from 'config';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { wishlistErrorAnalytics } from '~/utils/AnalyticsUtil.js';

const { isWishlistLoading, numberOfWishlistItems, currentWishlistItems } = useWishlistDetails();

const getAllListsStore = useGetAllListsStore();
const getSelectedListStore = useGetSelectedListStore();
const filterAndSortWishlist = useWishlistFilterSort();

const hasError = computed(() => getAllListsStore.userListsError || getSelectedListStore.userSelectedListsError);
const hasErrorDetails = computed(() => getAllListsStore.userListsErrorDetails || getSelectedListStore.userSelectedListsErrorDetails);

watch(
  hasError,
  (newError) => {
    if (newError) {
      const errorData = hasErrorDetails.value || newError;
      wishlistErrorAnalytics(errorData, fireTag);
    }
  },
  { immediate: true },
);

const guestPageTitle = 'Your temporary wish list';
const breadcrumbItems = computed(() => [
  {
    label: 'My Account',
    url: '/myaccount/home',
  },
  {
    label: guestPageTitle,
  },
]);

const filteredWishlistItems = ref([]);
const numberOfFilteredItems = computed(() => filteredWishlistItems.value.length);

const lastFilterSortSelection = ref(null);

watch(
  () => currentWishlistItems.value,
  (newItems) => {
    if (newItems) {
      if (lastFilterSortSelection.value) {
        const { selectedFilters, sortValue } = lastFilterSortSelection.value;
        filteredWishlistItems.value = filterAndSortWishlist(newItems, selectedFilters, sortValue);
      } else {
        filteredWishlistItems.value = [...newItems];
      }
    }
  },
  { immediate: true },
);

const addToBagError = ref('');

const clearAddToBagError = () => {
  addToBagError.value = '';
};

const handleAddToBagError = (message) => {
  addToBagError.value = message;
};

watch(addToBagError, (hasAddToBagError) => {
  if (hasAddToBagError) {
    nextTick(() => {
      document.addEventListener('click', clearAddToBagError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  } else {
    document.removeEventListener('click', clearAddToBagError);
  }
});

onUnmounted(() => {
  document.removeEventListener('click', clearAddToBagError);
});

const handleFilterChange = (filteredItems, selection) => {
  filteredWishlistItems.value = filteredItems;
  if (selection) {
    lastFilterSortSelection.value = selection;
  }
};

const goToSignIn = () => {
  const redirectUrl = `${window.location.origin}${window.location.pathname}`;
  Cookie.set('FORWARDPAGE_KEY', redirectUrl);
  window.location.assign('/account/signin');
};

const goToCreateAccount = () => {
  Cookie.set('FORWARDPAGE_KEY', window.location.href);
  window.location.assign('/account/createaccount');
};
</script>

<style lang="scss" scoped>
.guest-content-block {
  border: $white;
  background-color: $background-color;
}
.action-button {
  display: flex;
  justify-content: center;
  gap: rem-calc(8);
}
.product-grid-x {
  align-items: stretch;
}
.wishlist-header-container {
  row-gap: rem-calc(8);
  column-gap: rem-calc(8);
}
.column-gap {
  column-gap: rem-calc(8);
}

.error-container {
  margin-bottom: rem-calc(112);
  @include breakpoint(large) {
    margin-bottom: rem-calc(400);
  }
}
.wishlist-grid-container {
  min-height: 50vh;
}
</style>
